import { api } from "@/api/client";

// Namespaces the Partner Agency endpoints on the shared axios instance (cookies work generically
// for the partner_agency role — see api/client.js). Data lives under /v1/build/agency/*, the
// dedicated password auth under /v1/agency-auth/*.
const B = "/v1/build";
const A = "/v1/agency-auth";

export const agencyApi = {
  // auth
  login: (email, password) => api.post(`${A}/login`, { email, password }),
  logout: () => api.post(`${A}/logout`),
  forgot: (email) => api.post(`${A}/forgot-password`, { email }),
  tokenStatus: (token, kind) => api.get(`${A}/token-status`, { params: { token, kind }, silent401: true }),
  setPassword: (token, password, kind) => api.post(`${A}/set-password`, { token, password, kind }),
  // portal data
  home: () => api.get(`${B}/agency/home`),
  notifications: () => api.get(`${B}/agency/notifications`),
  myProjects: () => api.get(`${B}/agency/my-projects`),
  workspace: (pid) => api.get(`${B}/agency/projects/${pid}/workspace`),
  openItems: (pid) => api.get(`${B}/agency/projects/${pid}/open-items`),
  createOpenItem: (pid, body) => api.post(`${B}/agency/projects/${pid}/open-items`, body),
  commentItem: (itemId, text) => api.post(`${B}/agency/open-items/${itemId}/comments`, { text }),
  comments: (pid, entityRef) => api.get(`${B}/agency/projects/${pid}/comments`, { params: entityRef ? { entity_type: entityRef.type, entity_id: entityRef.id } : {} }),
  addComment: (pid, text, entityRef) => api.post(`${B}/agency/projects/${pid}/comments`, { text, entity_ref: entityRef }),
  crew: (pid) => api.get(`${B}/agency/projects/${pid}/crew`),
  addCandidate: (reqId, freelancerId) => api.post(`${B}/agency/crew-requirements/${reqId}/candidates`, { freelancer_id: freelancerId }),
  removeSlot: (slotId) => api.delete(`${B}/agency/crew-slots/${slotId}`),
  submitReq: (reqId) => api.post(`${B}/agency/crew-requirements/${reqId}/submit`),
  createRequirement: (pid, body) => api.post(`${B}/agency/projects/${pid}/crew-requirements`, body),
  documents: (pid) => api.get(`${B}/agency/projects/${pid}/documents`),
  sharedDocuments: () => api.get(`${B}/agency/documents`),
  discovery: (params) => api.get(`${B}/agency/discovery`, { params }),
  // cost sheet (Screen 08)
  quote: (pid) => api.get(`${B}/agency/projects/${pid}/quote`),
  costSheetVersions: (pid) => api.get(`${B}/agency/projects/${pid}/cost-sheet/versions`),
  costSheetComments: (pid) => api.get(`${B}/agency/projects/${pid}/cost-sheet/comments`),
  addCostSheetComment: (pid, text, lineId) => api.post(`${B}/agency/projects/${pid}/cost-sheet/comments`, { text, line_id: lineId || null }),
  requestCostSheetChanges: (pid, note) => api.post(`${B}/agency/projects/${pid}/cost-sheet/request-changes`, { note: note || "" }),
  approveCostSheet: (pid) => api.post(`${B}/agency/projects/${pid}/cost-sheet/approve`),
  // portal-wide "reach your PM" contact
  pmContact: () => api.get(`${B}/agency/pm-contact`),
  // Production Cost Estimator (Phase 1, 2026-09)
  estimatorConfig: () => api.get(`${B}/agency/estimator/config`),
  estimatorPrefill: (productionType, brief, cameraSetup, shootDays) => {
    const params = { production_type: productionType };
    if (brief) params.brief = brief;
    if (cameraSetup) params.camera_setup = cameraSetup;
    if (shootDays) params.shoot_days = shootDays;
    return api.get(`${B}/agency/estimator/prefill`, { params });
  },
  estimatorCatalog: (q) => api.get(`${B}/agency/estimator/catalog`, { params: q ? { q } : {} }),
  estimatorSubmit: (body) => api.post(`${B}/agency/estimator/submit`, body),
  // My Quote Requests (PM Feedback Batch 1, item 1, 2026-09)
  myQuoteRequests: () => api.get(`${B}/agency/quote-requests`),
  myQuoteRequest: (id) => api.get(`${B}/agency/quote-requests/${id}`),
  // Archives — canonical Video Links Master Library, agency-visible subset (PM Feedback Batch 2, item 3, 2026-09)
  archives: (category) => api.get(`${B}/agency/archives`, { params: category ? { category } : {} }),
  // Behind the Scenes (Agency Portal Restyle Wave 1, 2026-10)
  btsMedia: (pid) => api.get(`${B}/agency/projects/${pid}/bts-media`),
  btsMediaFileUrl: (pid, mediaId) => `${B}/agency/projects/${pid}/bts-media/${mediaId}/file`,
  // Recce (Wave R3+R4, 2026-10) — agency sees nothing for a recce until it's Shared/Final.
  // `recceId` (fix/recce-r3r4-cursor, L4) is optional on every read below — omitted, the
  // server falls back to "most recently shared recce" (unchanged default); passed, it picks
  // that SPECIFIC recce, powering the switcher once a project has more than one Shared/Final
  // recce at once.
  recces: (pid) => api.get(`${B}/agency/projects/${pid}/recces`),
  recce: (pid, recceId) => api.get(`${B}/agency/projects/${pid}/recce`, { params: recceId ? { recce_id: recceId } : {} }),
  recceLocation: (pid, locationId, recceId) => api.get(`${B}/agency/projects/${pid}/recce/locations/${locationId}`, { params: recceId ? { recce_id: recceId } : {} }),
  recceThumbUrl: (pid, photoId) => `${B}/agency/projects/${pid}/recce/photos/${photoId}/thumb`,
  recceWebUrl: (pid, photoId) => `${B}/agency/projects/${pid}/recce/photos/${photoId}/web`,
  setLocationPreference: (pid, locationId, preference) => api.post(`${B}/agency/projects/${pid}/recce/locations/${locationId}/preference`, { preference }),
  setFramingResponse: (pid, optionId, response) => api.post(`${B}/agency/projects/${pid}/recce/framing-options/${optionId}/response`, { response }),
  // Recce PDF (Wave R5, 2026-10) — ready-only versions, never via the Document Room picker.
  // `recceId` optional, same L4 convention as every other recce read above.
  recceePdfVersions: (pid, recceId) => api.get(`${B}/agency/projects/${pid}/recce/pdf/versions`, { params: recceId ? { recce_id: recceId } : {} }),
  recceePdfFileUrl: (pid, versionId, recceId) => `${B}/agency/projects/${pid}/recce/pdf/versions/${versionId}/file${recceId ? `?recce_id=${encodeURIComponent(recceId)}` : ""}`,
};

const KNOWN_ERROR_CODES = {
  geography_not_confirmed: "Your agency's billing region isn't confirmed yet — please contact your NJM PM before requesting a quote.",
  production_type_not_found: "This production type isn't available right now. Please refresh and try again.",
  package_not_found: "We couldn't load pricing for this production type. Please contact your NJM PM.",
  preview_is_read_only: "You're viewing a preview account, which can't submit requests.",
};

export function apiErr(e) {
  if (!e?.response) return "Couldn't reach NJM — please check your connection and try again.";
  const d = e.response.data?.detail;
  if (!d) return `Something went wrong (error ${e.response.status}). Please try again, or contact your NJM PM if it keeps happening.`;
  if (typeof d === "string") return d;
  if (d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  if (d.code) return KNOWN_ERROR_CODES[d.code] || `Something went wrong (${d.code}). Please try again, or contact your NJM PM if it keeps happening.`;
  return `Something went wrong (error ${e.response.status}). Please try again, or contact your NJM PM if it keeps happening.`;
}
